/**
 * @fileoverview Main application layout with responsive sidebar navigation.
 * Handles role-based menu items and collapsible submenus.
 *
 * Menu structure varies by role:
 * - Technician: Job Board, My Profile only
 * - Admin: Full access including Payments submenu, Admin Board, Invoices, etc.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import websocketService from '../services/websocketService';
import alertService from '../services/alertService';
import { store } from '../store/store';
import { setToken } from '../store/slices/authSlice';
import {
  AppBar,
  Backdrop,
  Badge,
  Box,
  Button,
  Collapse,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Menu as MenuIcon,
  People,
  Logout,
  CalendarToday,
  Email,
  Engineering,
  Assignment,
  ChevronLeft,
  ChevronRight,
  AdminPanelSettings,
  Payment,
  Person,
  Receipt,
  Settings,
  Sms,
  ExpandMore,
  ExpandLess,
  Refresh,
  Extension,
  Warning,
  SystemUpdateAlt,
  MenuBook,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../store/slices/authSlice';

/** @constant {number} Full drawer width in pixels */
const drawerWidth = 240;
/** @constant {number} Collapsed drawer width in pixels */
const collapsedDrawerWidth = 60;

/**
 * Returns menu items based on user role.
 *
 * @param {string} userRole - User role ('admin' or 'technician')
 * @returns {Array<{text: string, icon: JSX.Element, path?: string, children?: Array}>}
 *          Array of menu items, may include nested children for submenus
 */
const getMenuItems = (userRole) => {
  // Technician menu items - only job board and profile
  if (userRole === 'technician') {
    return [
      { text: 'Job Board', icon: <Assignment />, path: '/job-board' },
      { text: 'My Profile', icon: <Person />, path: '/profile' },
    ];
  }

  // Admin menu items - full access
  const baseItems = [
    { text: 'Bookings', icon: <CalendarToday />, path: '/booking' },
    { text: 'Email Processor', icon: <Email />, path: '/booking/email-processor' },
    { text: 'Job Board', icon: <Assignment />, path: '/job-board' },
    { text: 'Technicians', icon: <Engineering />, path: '/technicians' },
    { text: 'Customers', icon: <People />, path: '/customers' },
  ];

  // Add admin-only menu items
  if (userRole === 'admin') {
    baseItems.splice(3, 0, { // Insert after Job Board
      text: 'Admin Board',
      icon: <AdminPanelSettings />,
      path: '/admin-board'
    });
    baseItems.splice(4, 0, { // Insert after Admin Board
      text: 'Payments',
      icon: <Payment />,
      children: [
        { text: 'Pending', path: '/payments/pending' },
        { text: 'Paid', path: '/payments/paid' },
      ],
    });
    baseItems.push({ // Add to end
      text: 'Chat',
      icon: <Sms />,
      path: '/chat'
    });
    baseItems.push({ // Add to end
      text: 'Attention',
      icon: <Warning />,
      path: '/alerts'
    });
    baseItems.push({ // Add to end
      text: 'Invoices',
      icon: <Receipt />,
      path: '/invoices'
    });
    baseItems.push({ // Add to end
      text: 'Integrations',
      icon: <Extension />,
      children: [
        { text: 'Book My Garage', path: 'https://bookmygarage.com/manage/8353/', external: true },
        { text: 'GSF', path: 'https://trade.gsfcarparts.com/', external: true },
        { text: 'Euro Car Parts', path: 'https://omnipart.eurocarparts.com/', external: true },
      ],
    });
    baseItems.push({ // Add to end
      text: 'Organization Settings',
      icon: <Settings />,
      path: '/organization-settings'
    });
    baseItems.push({ // Add to end - Training documentation
      text: 'Training',
      icon: <MenuBook />,
      path: '/docs'
    });
  }

  return baseItems;
};

const MainLayout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [drawerCollapsed, setDrawerCollapsed] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [smsToast, setSmsToast] = useState({ open: false, message: '', conversationId: null });
  const [unreadSmsCount, setUnreadSmsCount] = useState(0);
  const [unreadAlertCount, setUnreadAlertCount] = useState(0);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const token = useSelector((state) => state.auth.token);

  // Helper to check if JWT token is expired or about to expire (within 60 seconds)
  const isTokenExpired = useCallback((tokenString) => {
    if (!tokenString) return true;
    try {
      const payload = JSON.parse(atob(tokenString.split('.')[1]));
      const expiresAt = payload.exp * 1000; // Convert to milliseconds
      return Date.now() >= expiresAt - 60000; // Expired or expiring within 60 seconds
    } catch {
      return true;
    }
  }, []);

  // Refresh token and reconnect WebSocket
  const refreshTokenAndReconnect = useCallback(async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      dispatch(logout());
      navigate('/auth/login');
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/v1/auth/refresh/`,
        { refresh: refreshToken },
        { headers: { 'Content-Type': 'application/json' } }
      );

      const { access } = response.data;
      localStorage.setItem('token', access);
      store.dispatch(setToken(access));

      // Reconnect WebSocket with new token
      websocketService.disconnect();
      websocketService.connect(access);
    } catch (error) {
      // Refresh token also expired - log out user
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      dispatch(logout());
      navigate('/auth/login');
    }
  }, [dispatch, navigate]);

  // Version check function - reusable for polling and visibility change
  const checkVersion = useCallback(async () => {
    const STORAGE_KEY = 'app_build_time';
    try {
      // Add cache-busting query param to avoid browser caching
      const response = await fetch(`/version.json?t=${Date.now()}`);
      if (!response.ok) return;

      const data = await response.json();
      const serverBuildTime = data.buildTime;
      const storedBuildTime = localStorage.getItem(STORAGE_KEY);

      if (!storedBuildTime) {
        // First visit, store current version
        localStorage.setItem(STORAGE_KEY, serverBuildTime);
      } else if (storedBuildTime !== serverBuildTime) {
        // New version deployed
        setUpdateAvailable(true);
      }
    } catch (error) {
      // Silently fail - version check is not critical
      console.debug('Version check failed:', error);
    }
  }, []);

  // Handle tab visibility change - refresh token, reconnect, and check version
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        const currentToken = localStorage.getItem('token');

        if (isTokenExpired(currentToken)) {
          // Token expired, try to refresh
          await refreshTokenAndReconnect();
        } else if (!websocketService.isConnected()) {
          // Token valid but WebSocket disconnected, reconnect
          websocketService.connect(currentToken);
        }

        // Check for app updates when tab becomes visible
        checkVersion();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isTokenExpired, refreshTokenAndReconnect, checkVersion]);

  // Initialize WebSocket connection on mount
  useEffect(() => {
    if (token) {
      websocketService.connect(token);
    }

    return () => {
      websocketService.disconnect();
    };
  }, [token]);

  // Version check - poll every 1 minute for new deployments
  useEffect(() => {
    const VERSION_CHECK_INTERVAL = 60 * 1000; // 1 minute

    // Check immediately on mount
    checkVersion();

    // Then check every 1 minute
    const intervalId = setInterval(checkVersion, VERSION_CHECK_INTERVAL);

    return () => clearInterval(intervalId);
  }, [checkVersion]);

  const handleRefreshApp = () => {
    // Update stored version before refresh to prevent loop
    localStorage.removeItem('app_build_time');
    window.location.reload();
  };

  // Global SMS notification listener
  useEffect(() => {
    const handleIncomingMessage = (message) => {
      // Only show toast and increment counter for inbound messages when not on chat page
      if (message.direction === 'inbound' && !location.pathname.startsWith('/chat')) {
        const preview = message.message?.substring(0, 50) + (message.message?.length > 50 ? '...' : '');
        setSmsToast({
          open: true,
          message: `New SMS: ${preview}`,
          conversationId: message.conversation,
        });
        setUnreadSmsCount((prev) => prev + 1);
      }
    };

    websocketService.on('message', handleIncomingMessage);

    return () => {
      websocketService.off('message', handleIncomingMessage);
    };
  }, [location.pathname]);

  // Clear unread count when navigating to chat page
  useEffect(() => {
    if (location.pathname.startsWith('/chat')) {
      setUnreadSmsCount(0);
    }
  }, [location.pathname]);

  // Fetch initial alert count
  useEffect(() => {
    const fetchAlertCount = async () => {
      try {
        const response = await alertService.getUnreadCount();
        setUnreadAlertCount(response.data.count);
      } catch (error) {
        console.error('Failed to fetch alert count:', error);
      }
    };

    if (token) {
      fetchAlertCount();
    }
  }, [token]);

  // WebSocket listener for alerts
  useEffect(() => {
    const handleAlertNew = () => {
      setUnreadAlertCount((prev) => prev + 1);
    };
    const handleAlertCount = (data) => {
      setUnreadAlertCount(data.count);
    };

    websocketService.on('alertNew', handleAlertNew);
    websocketService.on('alertCount', handleAlertCount);

    return () => {
      websocketService.off('alertNew', handleAlertNew);
      websocketService.off('alertCount', handleAlertCount);
    };
  }, []);

  // Clear alert badge when navigating to alerts page
  useEffect(() => {
    if (location.pathname === '/alerts') {
      setUnreadAlertCount(0);
    }
  }, [location.pathname]);

  const handleToastClose = () => {
    setSmsToast({ open: false, message: '', conversationId: null });
  };

  const handleToastClick = () => {
    if (smsToast.conversationId) {
      sessionStorage.setItem('chat:openConversationId', smsToast.conversationId);
      navigate('/chat');
    }
    handleToastClose();
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDrawerCollapse = () => {
    setDrawerCollapsed(!drawerCollapsed);
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/auth/login');
  };

  const handleMenuExpand = (menuText) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [menuText]: !prev[menuText],
    }));
  };

  const menuItems = getMenuItems(user?.role);

  const drawer = (
    <div>
      <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {!drawerCollapsed && (
          <Typography variant="h6" noWrap>
            Go Ops
          </Typography>
        )}
        <IconButton onClick={handleDrawerCollapse} sx={{ display: { xs: 'none', sm: 'block' } }}>
          {drawerCollapsed ? <ChevronRight /> : <ChevronLeft />}
        </IconButton>
      </Toolbar>
      <List>
        {menuItems.map((item) => (
          <React.Fragment key={item.text}>
            {item.children ? (
              // Menu item with submenu
              <>
                <Tooltip title={drawerCollapsed ? item.text : ''} placement="right">
                  <ListItem
                    button
                    onClick={() => handleMenuExpand(item.text)}
                    selected={item.children.some((child) => location.pathname.startsWith(child.path))}
                    sx={{
                      minHeight: 48,
                      justifyContent: drawerCollapsed ? 'center' : 'initial',
                      px: 2.5,
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: drawerCollapsed ? 0 : 3,
                        justifyContent: 'center',
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    {!drawerCollapsed && (
                      <>
                        <ListItemText primary={item.text} />
                        {expandedMenus[item.text] ? <ExpandLess /> : <ExpandMore />}
                      </>
                    )}
                  </ListItem>
                </Tooltip>
                <Collapse in={expandedMenus[item.text] && !drawerCollapsed} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.children.map((child) => (
                      <ListItem
                        key={child.text}
                        button
                        onClick={() => {
                          if (child.external) {
                            window.open(child.path, '_blank', 'noopener,noreferrer');
                          } else {
                            navigate(child.path);
                          }
                        }}
                        selected={!child.external && location.pathname.startsWith(child.path)}
                        sx={{
                          minHeight: 40,
                          pl: 6,
                        }}
                      >
                        <ListItemText primary={child.text} />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </>
            ) : (
              // Regular menu item without submenu
              <Tooltip title={drawerCollapsed ? item.text : ''} placement="right">
                <ListItem
                  button
                  onClick={() => navigate(item.path)}
                  selected={location.pathname.startsWith(item.path)}
                  sx={{
                    minHeight: 48,
                    justifyContent: drawerCollapsed ? 'center' : 'initial',
                    px: 2.5,
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: drawerCollapsed ? 0 : 3,
                      justifyContent: 'center',
                    }}
                  >
                    {item.text === 'Chat' && unreadSmsCount > 0 ? (
                      <Badge badgeContent={unreadSmsCount} color="error">
                        {item.icon}
                      </Badge>
                    ) : item.text === 'Attention' && unreadAlertCount > 0 ? (
                      <Badge badgeContent={unreadAlertCount} color="error">
                        {item.icon}
                      </Badge>
                    ) : (
                      item.icon
                    )}
                  </ListItemIcon>
                  {!drawerCollapsed && <ListItemText primary={item.text} />}
                </ListItem>
              </Tooltip>
            )}
          </React.Fragment>
        ))}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerCollapsed ? collapsedDrawerWidth : drawerWidth}px)` },
          ml: { sm: `${drawerCollapsed ? collapsedDrawerWidth : drawerWidth}px` },
          transition: 'margin 0.3s, width 0.3s',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {menuItems.find((item) => location.pathname.startsWith(item.path))?.text || 'Go Ops'}
          </Typography>
          <IconButton onClick={handleMenuClick} color="inherit">
            <Avatar sx={{ width: 32, height: 32 }}>
              {user?.first_name?.charAt(0) || 'U'}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem disabled>
              <Typography variant="body2">
                {user?.email || 'User'}
              </Typography>
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerCollapsed ? collapsedDrawerWidth : drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerCollapsed ? collapsedDrawerWidth : drawerWidth,
              transition: 'width 0.3s',
              overflowX: 'hidden',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerCollapsed ? collapsedDrawerWidth : drawerWidth}px)` },
          transition: 'width 0.3s',
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          overflow: 'hidden',
        }}
      >
        <Toolbar />
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <Outlet />
        </Box>
      </Box>

      {/* Global SMS Toast Notification */}
      <Snackbar
        open={smsToast.open}
        autoHideDuration={6000}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleToastClose}
          severity="info"
          sx={{ cursor: 'pointer', minWidth: 300 }}
          onClick={handleToastClick}
          icon={<Sms />}
        >
          {smsToast.message}
        </Alert>
      </Snackbar>

      {/* New Version Available - Full Screen Overlay */}
      <Backdrop
        open={updateAvailable}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 1,
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
        }}
      >
        <Paper
          elevation={8}
          sx={{
            p: 5,
            textAlign: 'center',
            maxWidth: 450,
            borderRadius: 3,
          }}
        >
          <SystemUpdateAlt
            sx={{
              fontSize: 80,
              color: 'primary.main',
              mb: 2,
            }}
          />
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
            Update Available
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            A new version has been deployed. Please refresh to continue using the application.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={handleRefreshApp}
            startIcon={<Refresh />}
            sx={{ px: 4, py: 1.5 }}
          >
            Refresh Now
          </Button>
        </Paper>
      </Backdrop>
    </Box>
  );
};

export default MainLayout;